import React, { useState, useEffect, useCallback } from 'react';
import PDFReader from '../visorPDF/components/PDFReader';
import { pdfFixture } from '../Busqueda/fixtures/pdf.fixture';
import './PdfViewer.css';

interface PdfViewerModalContentProps {
  nit?: string;
  contrato?: string;
  anexo?: string;
  anexoId?: number;
  onClose: () => void;
}

interface DocumentoResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    Base64: string;
    Extension: string;
    NomDoc: string;
  };
}

const PdfViewerModalContent: React.FC<PdfViewerModalContentProps> = ({
  nit,
  contrato,
  anexo,
  anexoId,
  onClose
}) => {
  const [pdfData, setPdfData] = useState<DocumentoResponse['Result'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPdfDocument = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Si estamos en modo desarrollo, usamos el fixture
      if (__DEV__ || import.meta.env.MODE === 'development') {
        console.log("Ambiente de pruebas - Usando fixture PDF", { 
          DEV_FLAG: __DEV__, 
          MODE: import.meta.env.MODE 
        });
        
        // Simulamos un pequeño delay para simular la llamada al API
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const data: DocumentoResponse = pdfFixture as DocumentoResponse;
        
        if (data.ResponseCode === 200 && data.Result) {
          // Validar que el Base64 no sea null
          if (!data.Result.Base64) {
            setError(window.CONST?.AnexosTarifarios?.errorDocumentoNoDisponible || 'El documento no está disponible en este momento.');
            setLoading(false);
            return;
          }
          setPdfData(data.Result);
        } else {
          setError(window.CONST?.AnexosTarifarios?.errorCargarDocumento || 'Error al cargar el documento PDF');
        }
        
        setLoading(false);
        return;
      }

      // En producción, llamamos al servicio real
      console.log("Ambiente productivo - Llamando al servicio PDF");
      
      const requestBody = {
        Nit: nit?.toString() || '',
        Contrato: contrato || '',
        Anexo: anexoId ? anexoId.toString() : (anexo || ''),
        Aprobado: true
      };

      const response = await fetch(
        '/_vti_bin/Servicios/ServicioTransar.svc/ObtenerDocumentoAnexoTarifario',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: DocumentoResponse = await response.json();
      
      if (data.ResponseCode === 200 && data.Result) {
        // Validar que el Base64 no sea null antes de establecer el estado
        if (!data.Result.Base64) {
          setError(window.CONST?.AnexosTarifarios?.errorDocumentoNoDisponible || 'El documento solicitado no está disponible.');
          setLoading(false);
          return;
        }
        setPdfData(data.Result);
      } else {
        setError(data.ResponseDetail || window.CONST?.AnexosTarifarios?.errorServicio || 'Error al obtener el documento');
      }
    } catch (err) {
      console.error('Error al cargar PDF:', err);
      setError(window.CONST?.AnexosTarifarios?.errorCargarDocumento || 'Error al cargar el documento PDF. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [nit, contrato, anexo, anexoId]);

  useEffect(() => {
    fetchPdfDocument();
  }, [fetchPdfDocument]);

  if (loading) {
    return (
      <div className="pdf-viewer-loading">
        <div className="pdf-viewer-spinner"></div>
        <p>Cargando documento...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-viewer-error">
        <span className="pdf-viewer-error-icon" role="img" aria-label="Warning">⚠️</span>
        <h3>Error al cargar el documento</h3>
        <p>{error}</p>
        <button 
          onClick={fetchPdfDocument}
          className="pdf-viewer-retry-button"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!pdfData) {
    return (
      <div className="pdf-viewer-error">
        <span className="pdf-viewer-error-icon" role="img" aria-label="Document">📄</span>
        <h3>No hay documento disponible</h3>
        <p>No se encontró el documento solicitado.</p>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-viewer-content">
        <PDFReader 
          base64Data={pdfData.Base64}
          fileName={pdfData.NomDoc}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

export default PdfViewerModalContent;